import { ENGINE_VERSION, isValidSeed, normalizeSeed } from '../engine';

/**
 * 도전장 링크 — 평문 (ADR-028 · UPLIFT §9.0). 서명·CRC·세대 테이블 없음.
 *   ?s=<sessionSeed 8자>&r=<runIndex>&v=<ENGINE_VERSION>-<packVersion>
 *   &sd=<sealed 0|1>&w=<solved 0..8>&lt=<lastSolveTick>&x=<reviveUsed 0|1>&i=<초대자 코드>&c=<채널>
 *   &pk=<팩 id · core 면 생략>  — 같은 판은 (엔진 버전, 팩 id, 팩 버전) 이 전부 같아야 한다(ADR-036)
 * 재현 키는 (sessionSeed, runIndex) 쌍이다 — 판 시드 하나만 실으면 수신자는 제3의 판을 한다(UPLIFT §9.1).
 */
export interface ChallengeRecord {
  readonly sealed: boolean;
  readonly solved: number;
  readonly lastSolveTick: number; // -1 = 없음
  readonly reviveUsed: number;
}

export interface ChallengeLink {
  readonly sessionSeed: string;
  readonly runIndex: number;
  readonly version: string; // `${ENGINE_VERSION}-${packVersion}`
  readonly pack: string; // 팩 id — 'core' | 'theme' …
  readonly record: ChallengeRecord;
  readonly inviter: string; // '' = 없음
  readonly channel: string; // '' = 없음
}

export const RUN_INDEX_MAX = 127;
/** SEAL §2.10 — 카톡 본문에 그대로 붙는 링크 상한(파생: 문안 4줄 + 링크 ≤ 카톡 1,400자 안전선). */
export const CHALLENGE_LINK_MAX_CHARS = 1400;

export function versionTag(packVersion: string): string {
  return `${ENGINE_VERSION}-${packVersion}`;
}

/** 링크가 못 박은 팩 버전 — `v=<ENGINE>-<packVersion>` 의 뒷부분. 없으면 null(최신을 쓴다) */
export function packVersionOf(link: ChallengeLink): string | null {
  const i = link.version.indexOf('-');
  if (i < 0) return null;
  const v = link.version.slice(i + 1);
  return /^\d+\.\d+\.\d+$/.test(v) ? v : null;
}

/** 팩 id — 소문자·숫자·하이픈 1~16자. 없거나 깨지면 'core'(P1 링크 호환). */
export function sanitizePackId(v: string | null): string {
  if (v === null) return 'core';
  const t = v.trim();
  return /^[a-z0-9-]{1,16}$/.test(t) ? t : 'core';
}

/** 초대자 코드 — 영숫자 1~8자만. 그 밖은 버린다(링크 오염 방어). */
export function sanitizeCode(v: string | null): string {
  if (v === null) return '';
  const t = v.trim();
  return /^[A-Za-z0-9]{1,8}$/.test(t) ? t : '';
}

export function buildChallengeLink(origin: string, link: ChallengeLink): string {
  const p = new URLSearchParams();
  p.set('s', link.sessionSeed);
  p.set('r', String(link.runIndex));
  p.set('v', link.version);
  p.set('sd', link.record.sealed ? '1' : '0');
  p.set('w', String(link.record.solved));
  p.set('lt', String(link.record.lastSolveTick));
  p.set('x', link.record.reviveUsed > 0 ? '1' : '0');
  if (link.pack !== 'core') p.set('pk', link.pack);
  if (link.inviter !== '') p.set('i', link.inviter);
  if (link.channel !== '') p.set('c', link.channel);
  return `${origin}?${p.toString()}`;
}

/**
 * 파싱은 예외를 던지지 않는다 — 범위 밖은 그 필드만 버리고, `s`·`r` 이 없거나 깨지면 null.
 * `?p=`·`?debug=` 는 절대 전파하지 않는다(호출부가 origin 만 넘긴다).
 */
export function parseChallengeLink(params: URLSearchParams): ChallengeLink | null {
  const s = params.get('s') ?? params.get('seed');
  const r = params.get('r');
  if (s === null || r === null || !isValidSeed(s)) return null;
  const runIndex = Number(r);
  if (!Number.isInteger(runIndex) || runIndex < 0 || runIndex > RUN_INDEX_MAX) return null;
  const int = (k: string, min: number, max: number, dflt: number): number => {
    const n = Number(params.get(k));
    return Number.isInteger(n) && n >= min && n <= max ? n : dflt;
  };
  return {
    sessionSeed: normalizeSeed(s),
    runIndex,
    version: params.get('v') ?? '',
    pack: sanitizePackId(params.get('pk')),
    record: {
      sealed: params.get('sd') === '1',
      solved: int('w', 0, 8, 0),
      lastSolveTick: int('lt', -1, 1_000_000, -1),
      reviveUsed: params.get('x') === '1' ? 1 : 0,
    },
    inviter: sanitizeCode(params.get('i')),
    channel: sanitizeCode(params.get('c')),
  };
}

/** 같은 판인가 — 엔진 버전·팩 id·팩 버전이 전부 같아야 비교한다. 아니면 목표를 숨기고 그냥 판만 연다. */
export function isSameVersion(link: ChallengeLink, packVersion: string, packId = 'core'): boolean {
  return link.version === versionTag(packVersion) && link.pack === packId;
}
